import React, { useState } from "react";
import { Button, Input, Label, Card, Badge, StatusBadge } from "../../components/fulkra";

/**
 * Proefpagina bij blok 1 van de restyling: de zes bouwstenen die in élk scherm zitten,
 * in alle standen die ze kunnen aannemen. Bedoeld om vroeg te kunnen bijsturen, vóór er
 * schermen omgebouwd worden.
 *
 * Deze pagina toont alleen componenten — geen gegevens uit de database, geen verzonnen
 * cijfers. De statussen hieronder zijn de echte statuswaarden uit de app.
 */

function Blok({ titel, toelichting, children }) {
  return (
    <section style={{ marginTop: "var(--ruimte-10)" }}>
      <h2 style={{ font: "var(--text-h2)", letterSpacing: "var(--ls-kop)" }}>{titel}</h2>
      {toelichting ? (
        <p style={{ marginTop: "var(--ruimte-3)", color: "var(--text-soft)", maxWidth: "60ch" }}>{toelichting}</p>
      ) : null}
      <div style={{ marginTop: "var(--ruimte-6)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-5)", alignItems: "flex-start" }}>
        {children}
      </div>
    </section>
  );
}

export default function StijlPreviewPage() {
  const [waarde, setWaarde] = useState("");

  return (
    <div className="fulkra" style={{ background: "var(--surface-page)", minHeight: "100vh", padding: "var(--ruimte-9) var(--ruimte-8)" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <p style={{ font: "var(--text-label)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "var(--text-soft)" }}>
          Blok 1 · huisstijl
        </p>
        <h1 style={{ font: "var(--text-h1)", letterSpacing: "var(--ls-kop)", marginTop: "var(--ruimte-3)" }}>
          De zes bouwstenen
        </h1>
        <p style={{ marginTop: "var(--ruimte-4)", color: "var(--text-soft)", maxWidth: "70ch" }}>
          Knop, invoerveld, label, kaart, badge en statuslabel — in de kleuren, letters en maten uit
          het designsysteem. Deze zes zitten in elk scherm; klopt hier iets niet, dan sleept dat door alles heen.
        </p>

        <Blok titel="Knoppen" toelichting="Vijf soorten, vier maten. Turkoois is de enige actiekleur; rood staat uitsluitend op iets dat verwijdert of onherstelbaar is.">
          <Button variant="primair">Aanmeetdag inplannen</Button>
          <Button variant="secundair">Annuleren</Button>
          <Button variant="stil">Filters wissen</Button>
          <Button variant="gevaar">Deelnemer verwijderen</Button>
          <Button variant="link">Bekijk klantprofiel</Button>
          <Button variant="primair" disabled>Bezig…</Button>
        </Blok>

        <Blok titel="Knopmaten" toelichting="De maat 'touch' is voor de telefoon: volle breedte, 44 pixels hoog, 16 pixels tekst.">
          <Button size="sm">Klein</Button>
          <Button size="md">Standaard</Button>
          <Button size="lg">Groot</Button>
          <div style={{ width: 260 }}><Button size="touch">Volledige breedte</Button></div>
        </Blok>

        <Blok titel="Invoervelden en labels" toelichting="Invoertekst staat op 16 pixels — kleiner en de telefoon zoomt bij het aantikken in. Elk label hoort via for/id aan zijn veld.">
          <div style={{ display: "grid", gap: "var(--ruimte-5)", width: 320 }}>
            <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <Label htmlFor="v1" required hint="Zoals bekend bij de werkgever">Achternaam</Label>
              <Input id="v1" value={waarde} onChange={(e) => setWaarde(e.target.value)} placeholder="De Vries" />
            </div>
            <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <Label htmlFor="v2">Klantnummer</Label>
              <Input id="v2" invalid defaultValue="onbekend" />
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }}>
                Dit klantnummer bestaat niet. Controleer het nummer of zoek op naam.
              </span>
            </div>
          </div>
        </Blok>

        <Blok titel="Kaarten" toelichting="Wit, dunne rand, zachte schaduw. Nooit een gekleurde linkerrand als accent.">
          <Card title="Zorggroep Meander" subtitle="Zorg · Apeldoorn" style={{ width: 320 }}
            actions={<Badge tone="positief">Actief</Badge>}
            footer={<Button variant="link">Beheren</Button>}>
            <p style={{ color: "var(--text-soft)" }}>
              Een kaart met kop, ondertitel, actie rechtsboven en een voettekst.
            </p>
          </Card>
          <Card style={{ width: 320 }}>
            <p style={{ color: "var(--text-soft)" }}>Een kale kaart, zonder kop.</p>
          </Card>
        </Blok>

        <Blok titel="Badges" toelichting="Zes tonen. Rood, oranje en groen alleen waar die betekenis geldt.">
          <Badge>Neutraal</Badge>
          <Badge tone="actie">Actie</Badge>
          <Badge tone="positief">Positief</Badge>
          <Badge tone="aandacht">Aandacht</Badge>
          <Badge tone="alarm">Alarm</Badge>
          <Badge tone="inkt">Inkt</Badge>
          <Badge pill>Pilvorm</Badge>
        </Blok>

        <Blok titel="Statuslabels" toelichting="De echte statussen uit de app. De teksten komen uit AdminUI, zodat er maar één plek is waar een status zijn naam krijgt.">
          {["aangemeld", "uitgenodigd", "baseline_voltooid", "aangemeten", "besteld", "geleverd",
            "week4_voltooid", "afgerond", "opgezegd", "no_show", "gefaald", "in_productie"].map((s) => (
            <StatusBadge key={s} value={s} />
          ))}
        </Blok>

        <Blok titel="Op een donker vlak" toelichting="Inkt is het merkanker: de zijbalk, het inlogscherm, de cijferband op het dashboard.">
          <div style={{ background: "var(--ink)", padding: "var(--ruimte-8)", borderRadius: "var(--radius-vlak)", width: "100%" }}>
            <p style={{ font: "var(--text-label)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "rgba(247,249,250,.65)" }}>
              Deelnemers actief
            </p>
            <p style={{ font: "var(--text-display)", color: "var(--text-inverse)", marginTop: "var(--ruimte-3)" }}>42</p>
            <div style={{ marginTop: "var(--ruimte-6)", display: "flex", gap: "var(--ruimte-5)" }}>
              <Button variant="primair">Primaire knop</Button>
              <Badge tone="inkt">Op inkt</Badge>
            </div>
          </div>
        </Blok>
      </div>
    </div>
  );
}
